import type { CommentItem, DashboardStats, PhotoPost, ReactionType } from '@/types';
import { getAdminPassword, hasAdminPassword } from '@/lib/admin-auth';

const json = async <T>(input: RequestInfo, init?: RequestInit): Promise<T> => {
  const response = await fetch(input, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Request failed');
  }

  return response.json() as Promise<T>;
};

const getAdminHeaders = () => {
  if (!hasAdminPassword()) {
    throw new Error('Admin belum di-unlock.');
  }

  return {
    'x-admin-password': getAdminPassword(),
  };
};

export const api = {
  getFeed: () => json<{ items: PhotoPost[] }>('/api/posts'),
  getComments: (postId: string) =>
    json<{ items: CommentItem[] }>(`/api/posts/${postId}/comments`),
  createComment: (postId: string, payload: { authorName: string; body: string; turnstileToken: string }) =>
    json<{ item: CommentItem }>(`/api/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  reactToPost: (postId: string, reaction: ReactionType) =>
    json<{ item: PhotoPost }>(`/api/posts/${postId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ reaction }),
    }),
  getStats: () =>
    json<{ stats: DashboardStats }>('/api/admin/stats', {
      headers: getAdminHeaders(),
    }),
  uploadPost: async (file: File, caption: string) => {
    const form = new FormData();
    form.append('image', file);
    form.append('caption', caption);

    const response = await fetch('/api/admin/upload', {
      method: 'POST',
      headers: getAdminHeaders(),
      body: form,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Upload failed');
    }

    return response.json() as Promise<{ success: true; id: string; imageKey: string }>;
  },

  deletePost: (postId: string) =>
    json<{ success: true }>(`/api/admin/posts/${postId}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    }),
};
